import railsPostFetch from '@aytm/lib/common/rails-post-fetch'
import mapMessages from '../../common/mapMessages'
import { generateUID } from '@aytm/lib/common/uid'
import { ChatMessage } from '../../types'
import ChatError from '../../common/ChatError'
import { API_SEND_ENDPOINT } from './const'
import { SKIPPER_AUTHOR_ID } from '../../const'
import { SendParams, AssistantSendResponse, SharedAssistantState } from './types'
import { trackPrompt, trackAgentResponses } from '../../common/pendoTracking'

export const generalSkipperErrorMessage = {
  error: true,
  id: generateUID(),
  timestamp: Date.now(),
  text: 'Arr, the message bottle did not reach its destination.\n\n[Try again](#resend_last_message)',
  author: SKIPPER_AUTHOR_ID
}
export const authErrorMessage = {
  error: true,
  id: generateUID(),
  timestamp: Date.now(),
  text: 'Oops — your chat session took a little nap! [Refresh](#reload_page) the page to wake it up.',
  author: SKIPPER_AUTHOR_ID
}

export default async function send<T extends SharedAssistantState>(
  message: string | ChatMessage,
  { assistantState, update, awaitContext, debugName = 'ChatAssistant', suggestedPrompt = false }: SendParams<T>
): Promise<ChatError | undefined> {
  const messageToSend: ChatMessage =
    'string' === typeof message
      ? {
          id: generateUID(),
          timestamp: Date.now(),
          text: message,
          author: assistantState.userName || 'User'
        }
      : {
          ...message,
          author: message.author || assistantState.userName || 'user'
        }

  trackPrompt({
    domain: assistantState.domain,
    conversationId: assistantState.chatID,
    messageId: messageToSend.id,
    content: messageToSend.text,
    suggestedPrompt
  })

  if ('development' === process.env.NODE_ENV) {
    // eslint-disable-next-line no-console
    console.debug(`${debugName}: send`, messageToSend)
  }

  // filter out error messages
  assistantState.messages = assistantState.messages.filter(message => !message.error)

  // adding ~~~attachments block for UI-side message
  const messageForDisplay = { ...messageToSend }
  const attachments = messageToSend.attachments as
    | Array<{ url: string; type: string; name: string; asset_id?: number | null }>
    | undefined
  if (attachments && attachments.length > 0) {
    const attachmentsData = attachments.map(att => {
      return {
        name: att.name,
        urls: {
          original: att.url,
          ...(att.type === 'image' || att.type === 'video' ? { thumbnail: att.url } : {})
        },
        type: att.type,
        asset_id: att.asset_id || null
      }
    })
    messageForDisplay.text = `${messageToSend.text}\n\n~~~attachments\n${JSON.stringify(attachmentsData)}\n~~~`
  }

  assistantState.messages = [...assistantState.messages, messageForDisplay]
  assistantState.inputReady = false
  update()

  try {
    const sendContext = await awaitContext({ assistantState }).catch(error => {
      throw error instanceof ChatError ? error : new ChatError(String(error))
    })

    // Consume-once: the guided-reports entry intent describes THIS turn only, so it
    // is read into the body and cleared before the request fires (never replayed).
    const guidedReport = assistantState.pendingGuidedReport
    assistantState.pendingGuidedReport = null

    const sendParams = {
      domain: assistantState.domain,
      message: messageToSend,
      ids: assistantState.messages.map(m => m.id),
      context: sendContext,
      ...(guidedReport ? { guided_report: guidedReport } : {})
    }

    const sendResponse = await railsPostFetch(
      API_SEND_ENDPOINT.URL.replace('{chatID}', String(assistantState.chatID)),
      {
        method: API_SEND_ENDPOINT.method,
        body: JSON.stringify(sendParams)
      }
    )

    if (401 === sendResponse.status) {
      throw new ChatError('Unauthorized')
    }
    if (200 !== sendResponse.status) {
      throw new ChatError('Failed to send message')
    }

    const response = (await sendResponse.json()) as AssistantSendResponse
    if (Array.isArray(response.messages)) {
      assistantState.messages = mapMessages(response.messages, assistantState.messages)
      trackAgentResponses({
        domain: assistantState.domain,
        conversationId: assistantState.chatID,
        messages: assistantState.messages
      })
    }

    if (null != response.inputReady) {
      assistantState.inputReady = response.inputReady
    }

    update()

    if ('development' === process.env.NODE_ENV) {
      // eslint-disable-next-line no-console
      console.debug(`${debugName}: send done`)
    }
  } catch (error) {
    if ('development' === process.env.NODE_ENV) {
      // eslint-disable-next-line no-console
      console.error(`${debugName}: send error`, (error as ChatError)?.message, (error as ChatError)?.payload)
    }

    const authError = 'Unauthorized' === (error as ChatError)?.message

    assistantState.messages = [...assistantState.messages, authError ? authErrorMessage : generalSkipperErrorMessage]
    assistantState.inputReady = !authError
    update()

    return error instanceof ChatError ? error : new ChatError(error instanceof Error ? error.message : 'Unknown error')
  }

  return undefined
}
